/**
 * Google Analytics 4 Utility Library
 * Core functions for tracking events, page views, and consent management
 *
 * @module lib/analytics
 * @description Provides GA4 tracking with consent management for GDPR/CCPA compliance
 */

// Configuration
const GA_MEASUREMENT_ID = process.env.GA_MEASUREMENT_ID || "G-W8DX96ES7R";
const IS_PRODUCTION = process.env.NODE_ENV === "production";
const DEBUG_MODE = process.env.GA_DEBUG === "true";

// Storage keys
const CONSENT_KEY = "mb_analytics_consent";
const CONSENT_SHOWN_KEY = "mb_consent_shown";

/**
 * Check if user has given consent for analytics
 */
export const hasAnalyticsConsent = (): boolean => {
  if (typeof window === "undefined") return false;
  const consent = localStorage.getItem(CONSENT_KEY);
  return consent === "granted";
};

/**
 * Check if consent banner has been shown
 */
export const hasConsentBeenShown = (): boolean => {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(CONSENT_SHOWN_KEY) === "true";
};

/**
 * Set analytics consent status
 */
export const setAnalyticsConsent = (granted: boolean): void => {
  if (typeof window === "undefined") return;

  localStorage.setItem(CONSENT_KEY, granted ? "granted" : "denied");
  localStorage.setItem(CONSENT_SHOWN_KEY, "true");

  // Analytics always runs (US-based). Cookie consent controls ad tracking only.
  updateAdConsent(granted ? "granted" : "denied");
};

// Ad tracking consent — analytics_storage stays granted always
const updateAdConsent = (status: "granted" | "denied"): void => {
  if (typeof window.gtag !== "function") return;

  window.gtag("consent", "update", {
    ad_storage: status,
    ad_user_data: status,
    ad_personalization: status,
  });
};

/**
 * Initialize GA4 tracking (call only after consent)
 */
export const initializeGA = (): void => {
  if (typeof window === "undefined") return;

  // Prevent duplicate initialization
  if (
    document.querySelector(
      `script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`
    )
  ) {
    return;
  }

  // Load gtag script
  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag function
  // Must use `arguments` (not rest params) — gtag.js expects Arguments objects in dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false, // We handle page views manually for SPA
    anonymize_ip: true,
    cookie_flags: "SameSite=None;Secure",
  });

  if (DEBUG_MODE) {
    console.log("[GA4] Initialized with ID:", GA_MEASUREMENT_ID);
  }
};

/**
 * Track page view
 */
export const trackPageView = (
  path: string,
  title: string,
  language: "en" | "es" = "en"
): void => {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
    language,
  });

  if (DEBUG_MODE) {
    console.log("[GA4] Page View:", { path, title, language });
  }
};

/**
 * Track custom event
 */
export const trackEvent = (
  eventName: string,
  params: Record<string, unknown> = {}
): void => {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", eventName, params);

  if (DEBUG_MODE) {
    console.log("[GA4] Event:", eventName, params);
  }
};

/**
 * Track form submission (generate_lead event)
 */
export const trackFormSubmission = (
  formName: string,
  language: "en" | "es" = "en"
): void => {
  trackEvent("generate_lead", {
    event_category: "Form",
    event_label: formName,
    form_name: formName,
    language,
  });
};

/**
 * Track CTA click
 */
export const trackCTAClick = (
  ctaText: string,
  destination: string,
  language: "en" | "es" = "en"
): void => {
  trackEvent("cta_click", {
    event_category: "CTA",
    event_label: ctaText,
    cta_text: ctaText,
    link_url: destination,
    language,
  });
};

/**
 * Track outbound link click
 */
export const trackOutboundLink = (url: string): void => {
  trackEvent("click", {
    event_category: "Outbound",
    event_label: url,
    link_url: url,
  });
};

/**
 * Track file download
 */
export const trackDownload = (fileName: string): void => {
  trackEvent("file_download", {
    event_category: "Download",
    event_label: fileName,
    file_name: fileName,
  });
};

/**
 * Track service interest (view_item event)
 */
export const trackServiceInterest = (
  serviceName: string,
  language: "en" | "es" = "en"
): void => {
  trackEvent("view_item", {
    event_category: "Service",
    event_label: serviceName,
    service_type: serviceName,
    language,
  });
};

/**
 * Track scroll depth milestone
 */
export const trackScrollDepth = (percentage: number): void => {
  trackEvent("scroll", {
    event_category: "Engagement",
    event_label: `${percentage}%`,
    value: percentage,
  });
};

/**
 * Track time on page (engagement metric)
 */
export const trackTimeOnPage = (seconds: number, pagePath: string): void => {
  trackEvent("engagement", {
    event_category: "Time on Page",
    event_label: pagePath,
    value: seconds,
  });
};

/**
 * Track phone call click
 */
export const trackPhoneClick = (phoneNumber: string): void => {
  trackEvent("click", {
    event_category: "Contact",
    event_label: "Phone Click",
    link_url: `tel:${phoneNumber}`,
  });
};

/**
 * Track email click
 */
export const trackEmailClick = (email: string): void => {
  trackEvent("click", {
    event_category: "Contact",
    event_label: "Email Click",
    link_url: `mailto:${email}`,
  });
};

/**
 * Track social media click
 */
export const trackSocialClick = (platform: string, url: string): void => {
  trackEvent("click", {
    event_category: "Social",
    event_label: platform,
    link_url: url,
  });
};

export { GA_MEASUREMENT_ID, CONSENT_KEY, CONSENT_SHOWN_KEY };
